import type { EventActivity, User } from '@paintscout/api'
import { usePollingInterval } from 'src/hooks/usePollingInterval'
import { useSaveEventMutation, useSearchActivitiesQuery, useSearchUsersQuery } from '@paintscout/api'
import { filterEvents } from '@paintscout/util'
import { mapActivitiesToCalendarEvents } from '@paintscout/util/builder'
import { useProcessEvent, useLocalStorage } from 'shared/hooks'
import { useDialogs, useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import type { Filter } from 'json-to-lucene'
import { jsonToLucene } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import type { CalendarEvent, EventType } from 'paintscout'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { View } from 'react-big-calendar'
import { useLocation } from 'react-router-dom'
import { Sentry } from 'src/util/appSentry'
import { EventDialog } from '../../dialogs'
import { CALENDAR_BUFFER_DAYS, getViewRange } from './calendarUtils'

interface CalendarContextValue {
  date: Date
  setDate: (date: Date) => void
  tab: 'calendar' | 'map' | 'schedule'
  loading: boolean
  fetchingIntegrationEvents: boolean
  view: View
  setView: (view: View) => void
  hideWeekends: boolean
  setHideWeekends: (hide: boolean) => void
  events: CalendarEvent[]
  allEvents: CalendarEvent[]
  updateEvent: (event: CalendarEvent, eventTemplate?: string) => void
  addEvent: (event: CalendarEvent, eventTemplate?: string) => Promise<void>
  deleteEvent: (event: CalendarEvent, eventTemplate?: string) => void
  onMarkerClick: (event: CalendarEvent) => void
  selectedUsers?: User[]
  setSelectedUsers?: (users: User[]) => void
}

const CalendarContext = createContext<CalendarContextValue | undefined>(undefined)

export const CalendarProvider: React.FC<{
  children: React.ReactNode
  tab?: 'calendar' | 'map' | 'schedule'
}> = ({ children, tab = 'map' }) => {
  const [rawView, setRawView] = useLocalStorage<View>('calendarView', 'month')
  const view = rawView === 'agenda' ? 'day' : rawView
  const setView = (newView: View) => setRawView(newView === 'agenda' ? 'day' : newView)

  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const { isSuperadmin, user } = useUser()
  const { permissionToViewAllEvents } = useUserPermissions()
  const { clientId } = useClientOptions()

  // CalendarContext stores events as their real UTC instants — no timezone
  // shifting in state. react-big-calendar's moment localizer (configured with
  // `moment.tz.setDefault(timezone)` in Calendar.tsx) positions events using
  // `localizer.diff(...)` which is timezone-aware, so it places them at the
  // company-tz hour without needing a pre-shifted Date. Storing real instants
  // means dialogs, popovers, and any other consumer that calls
  // `.tz(timezone)` on event.start/end shows the actual stored time.

  const location = useLocation()

  const [date, setDateState] = useState<Date>(new Date())
  const [users, setUsers] = useState<User[]>(null)
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [googleEvents, setGoogleEvents] = useState<CalendarEvent[]>([])
  const lastFetchedStart = useRef<Date | null>(null)
  const lastFetchedEnd = useRef<Date | null>(null)
  const lastFetchedUserIds = useRef<string>('')

  const [fetchedStartDate, setFetchedStartDate] = useState<Date>(() => {
    const d = new Date()
    d.setDate(d.getDate() - CALENDAR_BUFFER_DAYS)
    return d
  })
  const [fetchedEndDate, setFetchedEndDate] = useState<Date>(() => {
    const d = new Date()
    d.setDate(d.getDate() + CALENDAR_BUFFER_DAYS)
    return d
  })

  const setDate = setDateState
  const [hideWeekends, setHideWeekends] = useState(localStorage.getItem('calendarHideWeekends') !== 'true')
  const [loading, setLoading] = useState(true)
  const { processEvent } = useProcessEvent()
  const { enqueueSnackbar } = useSnackbar()
  const [fetchingIntegrationEvents, setFetchingIntegrationEvents] = useState(false)

  const { openDialog, dismissDialog } = useDialogs()

  async function getGoogleCalendarEvents() {
    setFetchingIntegrationEvents(true)
    const { result, error } = await processEvent({
      type: 'get-all-events' as EventType,
      provider: 'google-calendar',
      params: {
        userIds: !permissionToViewAllEvents ? [user.user_id] : users.map((user) => user.user_id),
        date
      }
    })

    setFetchingIntegrationEvents(false)
    if (error) {
      console.error('Error fetching Google Calendar events:', error)
      return
    }
    if (users && result?.result) {
      setGoogleEvents(mapActivitiesToCalendarEvents(result.result as EventActivity[], users))
    }
  }

  useEffect(() => {
    if (!users || users.length === 0) {
      return
    }
    const currentUserIds = !permissionToViewAllEvents ? [user.user_id] : users.map((u) => u.user_id)
    const userIdsKey = [...currentUserIds].sort().join(',')
    const { start: viewStart, end: viewEnd } = getViewRange(date, view)
    const cachedStart = lastFetchedStart.current
    const cachedEnd = lastFetchedEnd.current
    const outOfRange =
      !cachedStart ||
      !cachedEnd ||
      viewStart.getTime() < cachedStart.getTime() ||
      viewEnd.getTime() > cachedEnd.getTime()
    const usersChanged = lastFetchedUserIds.current !== userIdsKey
    if (outOfRange || usersChanged) {
      getGoogleCalendarEvents()
      // Mirror the backend window in get-all-events.ts: [date − 1 month, date + 1 month].
      lastFetchedStart.current = new Date(date.getFullYear(), date.getMonth() - 1, 1, 0, 0, 0, 0)
      lastFetchedEnd.current = new Date(date.getFullYear(), date.getMonth() + 2, 0, 23, 59, 59, 999)
      lastFetchedUserIds.current = userIdsKey
    }
  }, [date, view, users, permissionToViewAllEvents, user])

  const handleSetWeekends = (value: boolean) => {
    localStorage.setItem('calendarHideWeekends', value ? 'false' : 'true')
    setHideWeekends(value)
  }

  const filters: Filter[] = [
    {
      id: 'details.activityType',
      type: 'equal',
      value: 'event'
    } as Filter,
    {
      id: 'trashed',
      type: 'equal',
      value: true,
      not: true
    } as Filter,
    {
      id: 'details.production',
      type: 'equal',
      value: true,
      not: true
    },
    ...(location.pathname.includes('/events')
      ? [
          {
            id: 'details.canceled',
            type: 'equal',
            value: true,
            not: true
          } as Filter
        ]
      : []),
    {
      id: 'details.end',
      type: 'greaterThan',
      value: fetchedStartDate.getTime()
    } as Filter,
    {
      id: 'details.start',
      type: 'lessThan',
      value: fetchedEndDate.getTime()
    } as Filter
  ]

  const { data: eventsData } = useSearchActivitiesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
    pollInterval: usePollingInterval(120000),
    variables: {
      query: jsonToLucene({ filters }),
      limit: null
    },
    onError: useCallback((error) => {
      console.error(error)
      enqueueSnackbar('Something went wrong, please try again later.', {
        variant: 'error',
        autoHideDuration: 3000
      })
      Sentry.captureException(error)
    }, [])
  })

  const usersQuery = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''

  const { data: usersData, loading: usersLoading } = useSearchUsersQuery({
    variables: {
      query: usersQuery,
      includeInactive: true
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const users = data?.searchUsers?.rows ?? []
      setUsers(users)
      if (users?.length === 1) {
        setSelectedUsers(users)
      } else {
        const matchingUser = users.find((_user) => _user.user_id === user?.user_id)
        setSelectedUsers(matchingUser ? [matchingUser] : [])
      }
    }
  })

  const [saveEvent] = useSaveEventMutation()

  // Surface a save/update/delete failure to the user and Sentry. Callers are
  // responsible for the matching state rollback before invoking this.
  const handleMutationError = (error: unknown, message: string) => {
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 4000 })
    Sentry.captureException(error)
  }

  const updateEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    const previousEvent = events.find((e) => e.id === event.id)
    const wasVisible = !!previousEvent
    let updateContact = false
    if (permissionToViewAllEvents || event.estimator?.id === user?.user_id) {
      setEvents((prevEvents) =>
        prevEvents.map((prevEvent) => {
          if (prevEvent.id === event.id) {
            if (prevEvent?.contact?.preferredContactMethod !== event?.contact?.preferredContactMethod) {
              // need to update this contact with the new preferred contact method
              updateContact = true
            }
            return { ...prevEvent, ...event }
          }
          return prevEvent
        })
      )
    } else {
      // can only see own events, so remove the event from the list if it was reassigned away
      setEvents((prevEvents) => prevEvents.filter((prevEvent) => prevEvent.id !== event.id))
    }
    try {
      const result = await saveEvent({
        variables: {
          event,
          eventTemplate
        },
        refetchQueries: [updateContact ? 'getContact' : null].filter(Boolean)
      })

      // Update with the server response to pick up server-computed values (e.g. geocode after address change)
      const savedActivity = result?.data?.saveEvent?.activity
      if (savedActivity) {
        const [updatedEvent] = mapActivitiesToCalendarEvents([savedActivity], users, true)
        if (updatedEvent) {
          setEvents((prevEvents) =>
            prevEvents.map((prevEvent) =>
              prevEvent.id === updatedEvent.id ? { ...prevEvent, ...updatedEvent } : prevEvent
            )
          )
        }
      }
    } catch (error) {
      // Roll back to the previous event so the calendar reflects what's actually saved.
      setEvents((prevEvents) => {
        const without = prevEvents.filter((e) => e.id !== event.id)
        return wasVisible ? [...without, previousEvent] : without
      })
      handleMutationError(error, 'Could not update event, please try again.')
    }
  }

  const addEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    const canSee = permissionToViewAllEvents || event.estimator?.id === user?.user_id
    if (canSee) {
      setEvents((prevEvents) => [...prevEvents, event])
    }

    try {
      const result = await saveEvent({
        variables: {
          event,
          eventTemplate
        }
      })
      // DO NOT add `refetchQueries: ['searchActivities']` here. The search
      // query is Elasticsearch-backed and the new doc may not be indexed yet
      // when the refetch runs. If the refetch returns without it, the
      // searchActivities useEffect replaces `events` and wipes our optimistic
      // add — the exact "event appears then disappears" bug this code fixes.
      // The 120s poll keeps us in sync with other clients.
      const savedActivity = result?.data?.saveEvent?.activity
      if (savedActivity && canSee) {
        const [savedEvent] = mapActivitiesToCalendarEvents([savedActivity], users, true)
        if (savedEvent) {
          setEvents((prevEvents) =>
            prevEvents.map((prevEvent) =>
              prevEvent.id === savedEvent.id ? { ...prevEvent, ...savedEvent } : prevEvent
            )
          )
        }
      }
    } catch (error) {
      setEvents((prevEvents) => prevEvents.filter((e) => e.id !== event.id))
      handleMutationError(error, 'Could not save event, please try again.')
    }
  }

  const deleteEvent = async (event: CalendarEvent, eventTemplate?: string) => {
    const previousEvent = events.find((e) => e.id === event.id)
    setEvents((prevEvents) => prevEvents.filter((prevEvent) => prevEvent.id !== event.id))

    try {
      await saveEvent({
        variables: {
          event: { ...event, canceled: true },
          eventTemplate
        }
      })
    } catch (error) {
      if (previousEvent) {
        setEvents((prevEvents) => [...prevEvents, previousEvent])
      }
      handleMutationError(error, 'Could not delete event, please try again.')
    }
  }

  const onMarkerClick = (event: CalendarEvent) => {
    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (event: CalendarEvent, eventTemplate: string) => {
        updateEvent(event, eventTemplate)
        dismissDialog()
      }
    })
  }

  // Expand fetch window when date or view changes
  useEffect(() => {
    const { start, end } = getViewRange(date, view)
    if (start.getTime() < fetchedStartDate.getTime() || end.getTime() > fetchedEndDate.getTime()) {
      setFetchedStartDate(new Date(start.getTime() - CALENDAR_BUFFER_DAYS * 86400000))
      setFetchedEndDate(new Date(end.getTime() + CALENDAR_BUFFER_DAYS * 86400000))
    }
    // fetchedStartDate/fetchedEndDate are intentionally included so the check always uses
    // the latest window values; expansion is idempotent so this doesn't cause infinite loops.
  }, [date, view, fetchedStartDate, fetchedEndDate])

  useEffect(() => {
    if (users) {
      // Default to the logged-in user once users are loaded in day view
      const matchingUser = users.find((_user) => _user.user_id === user?.user_id)
      setSelectedUsers(view === 'month' ? users : [matchingUser ?? users[0]])
    }
  }, [users])

  useEffect(() => {
    if (!users?.length) {
      return
    }

    if (view !== 'month') {
      if (selectedUsers?.length > 1) {
        const matchingUser = selectedUsers.find((_user) => _user.user_id === user?.user_id)
        setSelectedUsers([matchingUser ?? selectedUsers[0]])
      }
      return
    }

    if (permissionToViewAllEvents) {
      const activeUsers = users.filter((user) => user.app_metadata?.active)
      const hasSameUsers =
        activeUsers.length === (selectedUsers?.length ?? 0) &&
        activeUsers.every((activeUser) =>
          selectedUsers?.some((selectedUser) => selectedUser.user_id === activeUser.user_id)
        )

      if (!hasSameUsers) {
        setSelectedUsers(activeUsers)
      }
    }
  }, [permissionToViewAllEvents, user, users, view])

  // The !events.length guard was intentionally removed: it was blocking events from being
  // updated after the initial load, which is required for the sliding window re-fetches to
  // take effect. updateEvent/addEvent use refetchQueries so server data stays consistent.
  useEffect(() => {
    if (eventsData && usersData) {
      const realEvents = mapActivitiesToCalendarEvents(
        eventsData?.searchActivities?.activities as EventActivity[],
        usersData?.searchUsers?.rows as User[]
      )
      setEvents(realEvents)
      setLoading(false)
    }
  }, [eventsData, usersData])

  // Listen for BroadcastChannel messages to update the activities if a new event is created from the navbar
  useEffect(() => {
    if (typeof BroadcastChannel == 'undefined') {
      return
    }
    const channel = new BroadcastChannel('header-new-button')
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.event === 'new-calendar-event') {
        const newEvent = event.data.payload as EventActivity
        const [newCalendarEvent] = mapActivitiesToCalendarEvents([newEvent], users)
        setEvents((prevEvents) => {
          return [...prevEvents, newCalendarEvent]
        })
      }
    }
    channel.addEventListener('message', handleMessage)

    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [users])

  const filteredEvents = filterEvents({
    date,
    events: [...events, ...googleEvents],
    selectedUsers,
    users,
    view: view as 'month' | 'week' | 'day' | 'agenda'
  })

  return (
    <CalendarContext.Provider
      value={{
        date,
        setDate,
        tab,
        loading: loading || usersLoading,
        fetchingIntegrationEvents,
        view,
        setView,
        hideWeekends,
        setHideWeekends: handleSetWeekends,
        allEvents: events,
        events: filteredEvents,
        selectedUsers,
        setSelectedUsers,
        updateEvent,
        addEvent,
        deleteEvent,
        onMarkerClick
      }}
    >
      {children}
    </CalendarContext.Provider>
  )
}

export const useCalendar = (): CalendarContextValue => {
  const context = useContext(CalendarContext)
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider')
  }
  return context
}
